import { useTranslation } from "react-i18next";
import { Inbox, Sparkles } from "lucide-react";
import { EmptyState } from "@/components/vroom/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { GroupRow } from "./group-row.tsx";
import type { HoursEntriesAdminWeekResponse } from "@/api/generated/types.gen";
import type { StatusTab } from "./uren-status-tabs.tsx";

interface WeekTableProps {
  data: HoursEntriesAdminWeekResponse | undefined;
  dates: string[];
  tab: StatusTab;
  selectedKeys: Set<string>;
  onToggleSelect: (key: string) => void;
  isLoading?: boolean;
}

function groupKey(g: HoursEntriesAdminWeekResponse["groups"][number]): string {
  return g.kind === "worker"
    ? `worker:${g.worker.id}`
    : `wc:${g.workorder.id}:${g.crane?.id ?? "none"}`;
}

const NL_SHORT_DAY = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function TableHead({ dates }: { dates: string[] }) {
  return (
    <thead className="sticky top-0 bg-surface border-b border-line text-[9.5px] font-mono uppercase tracking-wider text-ink-500">
      <tr>
        <th className="text-left pl-3 py-2.5">Werknemer</th>
        <th className="text-left py-2.5">Werkorder</th>
        <th className="text-left py-2.5">Plaats</th>
        <th className="py-2.5"></th>
        <th className="text-left py-2.5">Kraan</th>
        {dates.map((d, i) => (
          <th key={d || i} className="text-right pr-2 py-2.5">
            {NL_SHORT_DAY[i]}
            <br />
            <span className="font-normal">{d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : ""}</span>
          </th>
        ))}
        <th className="text-right pr-3 py-2.5">Totaal</th>
      </tr>
    </thead>
  );
}

function SkeletonRows({ dayCount, rowCount }: { dayCount: number; rowCount: number }) {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, r) => (
        <tr key={r} className="border-b border-line/60">
          <td className="pl-3 py-3">
            <Skeleton className="h-4 w-32" />
          </td>
          <td className="py-3">
            <Skeleton className="h-4 w-16" />
          </td>
          <td className="py-3">
            <Skeleton className="h-4 w-24" />
          </td>
          <td className="py-3" />
          <td className="py-3">
            <Skeleton className="h-4 w-14" />
          </td>
          {Array.from({ length: dayCount }).map((__, d) => (
            <td key={d} className="pr-2 py-3">
              <Skeleton className="ml-auto h-4 w-8" />
            </td>
          ))}
          <td className="pr-3 py-3">
            <Skeleton className="ml-auto h-4 w-10" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function WeekTable({
  data,
  dates,
  tab,
  selectedKeys,
  onToggleSelect,
  isLoading,
}: WeekTableProps) {
  const { t } = useTranslation();

  if (!data) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <TableHead dates={dates} />
          <tbody>
            <SkeletonRows dayCount={dates.length} rowCount={isLoading ? 6 : 3} />
          </tbody>
        </table>
      </div>
    );
  }

  if (data.groups.length === 0) {
    const filteredKey = `admin.uren.empty.filtered-${tab}` as const;
    const allZero = Object.values(data.meta.counts).every((c) => c === 0);
    if (allZero) {
      return (
        <EmptyState
          variant={tab === "todo" ? "healthy" : "filtered"}
          size="medium"
          icon={tab === "todo" ? <Sparkles size={40} /> : <Inbox size={40} />}
          title={
            tab === "todo" ? t("admin.uren.empty.todo") : t(filteredKey, { week: data.isoWeek })
          }
        />
      );
    }
    return (
      <EmptyState
        variant="filtered"
        size="medium"
        icon={<Inbox size={40} />}
        title={t(filteredKey, { week: data.isoWeek })}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <TableHead dates={dates} />
        <tbody>
          {data.groups.map((g) => {
            const key = groupKey(g);
            return (
              <GroupRow
                key={key}
                group={g}
                weekDates={dates}
                related={data.meta.related}
                selected={selectedKeys.has(key)}
                onSelect={() => onToggleSelect(key)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { groupKey };
